import { useState } from 'react';
import { CartItem } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface CheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cartItems: CartItem[];
  onConfirmOrder: (shippingInfo: ShippingInfo) => void;
  shippingPrice?: number;
}

export interface ShippingInfo {
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
}

export function CheckoutDialog({ 
  open, 
  onOpenChange, 
  cartItems,
  onConfirmOrder,
  shippingPrice = 0
}: CheckoutDialogProps) {
  const [formData, setFormData] = useState<ShippingInfo>({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Iceland',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🚀 CheckoutDialog: Form submitted');
    console.log('📋 CheckoutDialog: Form data:', formData);
    setIsSubmitting(true);
    
    try {
      console.log('📞 CheckoutDialog: Calling onConfirmOrder...');
      await onConfirmOrder(formData);
      console.log('✅ CheckoutDialog: onConfirmOrder completed');
      // Form will be reset in parent component after redirect
    } catch (error) {
      console.error('❌ CheckoutDialog: Checkout error:', error);
      setIsSubmitting(false);
    }
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.selectedSize.price * item.quantity, 0);
  const shipping = 0; // Free shipping - artist covers the cost
  const total = subtotal + shipping;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] md:max-w-[700px] bg-white max-h-[85vh] overflow-y-auto relative p-6 md:p-8">
        <DialogHeader>
          <DialogTitle className="tracking-tight text-[19px] md:text-xl">Shipping Information</DialogTitle>
          <DialogDescription className="text-neutral-600 text-[15px] md:text-sm">
            Orders are printed on museum-quality fine art paper and shipped directly to you.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Order Summary */}
          <div className="border-t border-b border-neutral-100 py-4 space-y-2">
            <h4 className="text-[16px] md:text-sm tracking-wide mb-3 font-medium">Order</h4>
            {cartItems.map((item) => {
              const sizeInfo = item.photo.sizes?.find(s => s.sizeId === item.selectedSize.id);
              const available = sizeInfo?.available ?? 10;
              const isSoldOut = available <= 0;
              return (
              <div key={`${item.photo.id}-${item.selectedSize.id}`} className="flex items-center justify-between text-[15px] md:text-sm gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-neutral-600 truncate">
                    {item.photo.title} · {item.selectedSize.name} × {item.quantity}
                  </span>
                  {available < 10 && (
                    <span className={`shrink-0 px-2.5 py-0.5 text-[9px] tracking-[1.5px] uppercase font-light border ${
                      isSoldOut
                        ? 'border-red-300 text-red-400'
                        : available <= 3
                          ? 'border-yellow-400/40 text-yellow-600'
                          : 'border-neutral-300 text-neutral-400'
                    }`}>
                      {isSoldOut ? 'Sold Out' : `${available} left`}
                    </span>
                  )}
                </div>
                <span className="shrink-0">€{item.selectedSize.price * item.quantity}</span>
              </div>
              );
            })}
            <div className="flex justify-between pt-2 text-[15px] md:text-sm">
              <span className="text-neutral-600">Subtotal</span>
              <span className="text-neutral-600">€{subtotal}</span>
            </div>
            <div className="flex justify-between text-[15px] md:text-sm">
              <span className="text-neutral-600">Shipping</span>
              <span className="text-green-600 font-medium">FREE</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-neutral-100 text-[16px] md:text-base">
              <span className="tracking-wide font-medium">Total</span>
              <span className="tracking-wide font-medium">€{total}</span>
            </div>
          </div>

          {/* Shipping Form */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-[15px] md:text-sm tracking-wide">First Name</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  className="border-neutral-200 h-12 md:h-10 text-[16px] md:text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-[15px] md:text-sm tracking-wide">Last Name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                  className="border-neutral-200 h-12 md:h-10 text-[16px] md:text-base"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[15px] md:text-sm tracking-wide">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="border-neutral-200 h-12 md:h-10 text-[16px] md:text-base"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="text-[15px] md:text-sm tracking-wide">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                className="border-neutral-200 h-12 md:h-10 text-[16px] md:text-base"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city" className="text-[15px] md:text-sm tracking-wide">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                  className="border-neutral-200 h-12 md:h-10 text-[16px] md:text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postalCode" className="text-[15px] md:text-sm tracking-wide">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  required
                  className="border-neutral-200 h-12 md:h-10 text-[16px] md:text-base"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="country" className="text-[15px] md:text-sm tracking-wide">Country</Label>
              <Input
                id="country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                required
                className="border-neutral-200 h-12 md:h-10 text-[16px] md:text-base"
              />
            </div>
          </div>

          <div className="bg-neutral-50 p-4 rounded text-[15px] md:text-sm text-neutral-600 space-y-1">
            <p className="tracking-wide">Printed on museum-quality fine art paper</p>
            <p className="tracking-wide">Museum-quality archival standards</p>
            <p className="tracking-wide">Digital certificate of authenticity sent to email</p>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-12 md:h-10 text-[16px] md:text-base"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-black text-white hover:bg-neutral-800 h-12 md:h-10 text-[16px] md:text-base font-medium"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Opening payment...' : 'Continue to Payment'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CheckoutDialog;